/**
 * Admin · IT Solutions · Catalogue curation.
 *
 * - List all products (drafts + public).
 * - Create / edit modal with full editor (specs, gallery, hero, marketing).
 * - Toggle is_public + is_featured + featured_order.
 * - Delete (hard).
 *
 * No prices visible on the public page — but admins can record an
 * indicative "starting_from_aed" for internal context.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Search, X, Eye, EyeOff, Star,
  Loader2, Image as ImageIcon, Inbox, Tag, Laptop, ArrowUpRight, Save,
  Link2, Sparkles, AlertCircle, FileText,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRANDS = ['Lenovo', 'HP', 'Dell', 'Apple', 'Microsoft', 'Cisco'];
const CATEGORIES = [
  { key: 'business_laptops', label: 'Business Laptops' },
  { key: 'workstations', label: 'Workstations' },
  { key: 'monitors', label: 'Monitors' },
  { key: 'printers_peripherals', label: 'Printers & Peripherals' },
  { key: 'networking', label: 'Networking & WiFi' },
  { key: 'software_security', label: 'Software & Security' },
];

const ITCatalogueAdmin = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [editor, setEditor] = useState(null); // null | {} for new | {existing}
  const [galleryFor, setGalleryFor] = useState(null); // product whose gallery is being managed
  const [deleting, setDeleting] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [pdfMenuFor, setPdfMenuFor] = useState(null);  // { id, openUp } | null

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/it-catalogue/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data || []);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Load failed', description: e.response?.data?.detail || e.message });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/it-catalogue/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(r.data || []);
    } catch {
      setCompanies([]);
    }
  };

  useEffect(() => { fetchProducts(); fetchCompanies(); /* eslint-disable-next-line */ }, []);

  // Close the per-row PDF menu on outside click
  useEffect(() => {
    if (!pdfMenuFor) return undefined;
    const close = () => setPdfMenuFor(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [pdfMenuFor]);

  const downloadProductPdf = (productId, companyId) => {
    const url = `${API_URL}/api/admin/it-catalogue/${productId}/pdf?company_id=${companyId}`;
    // Use a temporary anchor so the JWT-protected route inherits the user's cookie/session
    // — actually FastAPI requires the bearer token. Use fetch + blob to attach the header.
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.click();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
      })
      .catch((e) => toast({ variant: 'destructive', title: 'PDF failed', description: e.message }));
  };

  const filtered = useMemo(() => {
    return rows.filter((p) => {
      if (filterBrand && p.brand !== filterBrand) return false;
      if (filterCategory && p.category !== filterCategory) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return [p.sku, p.name, p.short_description]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(s));
    });
  }, [rows, search, filterBrand, filterCategory]);

  const stats = useMemo(() => ({
    total: rows.length,
    published: rows.filter((p) => p.is_public).length,
    featured: rows.filter((p) => p.is_featured).length,
  }), [rows]);

  // ---- Dynamic taxonomies (canonical + whatever the admin has already added)
  const dynamicBrands = useMemo(() => {
    const set = new Set(BRANDS);
    rows.forEach((p) => { if (p.brand) set.add(p.brand); });
    return Array.from(set);
  }, [rows]);
  const dynamicCategories = useMemo(() => {
    const map = new Map();
    CATEGORIES.forEach((c) => map.set(c.key, c.label));
    const humanize = (k) => (k || '').split('_').filter(Boolean).map((p) => p[0].toUpperCase() + p.slice(1)).join(' ') || k;
    rows.forEach((p) => {
      if (p.category && !map.has(p.category)) map.set(p.category, humanize(p.category));
    });
    return Array.from(map, ([key, label]) => ({ key, label }));
  }, [rows]);

  const togglePublic = async (p) => {
    try {
      await axios.patch(`${API_URL}/api/admin/it-catalogue/${p.id}`,
        { is_public: !p.is_public }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: p.is_public ? 'Hidden from public' : 'Published', description: `${p.sku} — ${p.name}` });
      fetchProducts();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Toggle failed', description: e.response?.data?.detail || e.message });
    }
  };

  const toggleFeatured = async (p) => {
    try {
      await axios.patch(`${API_URL}/api/admin/it-catalogue/${p.id}`,
        { is_featured: !p.is_featured }, { headers: { Authorization: `Bearer ${token}` } });
      fetchProducts();
    } catch (_) { /* ignore */ }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API_URL}/api/admin/it-catalogue/${deleting.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Product deleted', description: `${deleting.sku} — ${deleting.name}` });
      setDeleting(null);
      fetchProducts();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="mb-4"><BackButton to="/admin/dashboard" label="Dashboard" /></div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Laptop className="w-6 h-6 text-[#023E8A]" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">IT Solutions · Catalogue</h1>
            <Badge className="bg-[#023E8A]/10 text-[#023E8A] border border-[#023E8A]/30 font-mono text-[10px]">P0.7</Badge>
          </div>
          <p className="text-sm text-[#6C757D]">
            Curate the public IT catalogue — products, brands, hero images, featured selections.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/it-catalogue/rfq">
            <Button variant="outline" data-testid="open-rfq-inbox-btn">
              <Inbox className="w-4 h-4 mr-2" /> RFQ Inbox
            </Button>
          </Link>
          <Button
            onClick={async () => {
              try {
                const r = await axios.get(
                  `${API_URL}/api/admin/it-catalogue/export-all`,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `it-catalogue-export-${stamp}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast({ title: 'Export ready', description: `${r.data.count} products downloaded` });
              } catch (e) {
                toast({ variant: 'destructive', title: 'Export failed', description: e.response?.data?.detail || e.message });
              }
            }}
            variant="outline"
            className="border-slate-700 text-slate-700 hover:bg-slate-700 hover:text-white font-bold"
            data-testid="export-catalogue-btn"
            title="Download every IT product as JSON — for migrating preview → production"
          >
            <FileText className="w-4 h-4 mr-2" /> Export JSON
          </Button>
          <Button
            onClick={() => document.getElementById('it-import-file-input')?.click()}
            variant="outline"
            className="border-violet-700 text-violet-700 hover:bg-violet-700 hover:text-white font-bold"
            data-testid="import-catalogue-btn"
            title="Upload a JSON export from another environment"
          >
            <Plus className="w-4 h-4 mr-2" /> Import JSON
          </Button>
          <input
            id="it-import-file-input"
            type="file"
            accept="application/json,.json"
            className="hidden"
            data-testid="import-catalogue-file"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              const proceed = window.confirm(
                `Import "${file.name}"?\n\n` +
                'Choose OK to ADD missing products only (safe).\n' +
                'Choose Cancel to abort.\n\n' +
                'To OVERWRITE existing products, hold Shift while clicking OK.',
              );
              if (!proceed) return;
              const overwrite = window.confirm(
                'OVERWRITE existing products with the file content?\n\n' +
                'OK = overwrite by id/SKU (destructive)\n' +
                'Cancel = add missing only (safe)',
              );
              try {
                const text = await file.text();
                const payload = JSON.parse(text);
                const r = await axios.post(
                  `${API_URL}/api/admin/it-catalogue/import-bulk?overwrite=${overwrite ? 'true' : 'false'}`,
                  payload,
                  { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
                );
                const d = r.data || {};
                toast({
                  title: 'Import complete',
                  description: `Created ${d.created} · Updated ${d.updated} · Skipped ${d.skipped} · Failed ${d.failed}`,
                });
                fetchProducts();
              } catch (err) {
                toast({ variant: 'destructive', title: 'Import failed', description: err.response?.data?.detail || err.message });
              }
            }}
          />
          <Button
            onClick={async () => {
              try {
                const r = await axios.post(
                  `${API_URL}/api/admin/it-catalogue/sync-master`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                toast({
                  title: 'Master registry synced',
                  description: `Created ${r.data.created} · Updated ${r.data.updated} · Skipped ${r.data.skipped}`,
                });
                fetchProducts();
              } catch (e) {
                toast({ variant: 'destructive', title: 'Sync failed', description: e.response?.data?.detail || e.message });
              }
            }}
            variant="outline"
            className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold"
            data-testid="sync-mpr-btn"
            title="Mirror every IT product into the Master Product Registry (P4)"
          >
            <Tag className="w-4 h-4 mr-2" /> Sync to MPR
          </Button>
          <Button
            onClick={() => setImportOpen(true)}
            variant="outline"
            className="border-[#023E8A] text-[#023E8A] hover:bg-[#023E8A] hover:text-white font-bold"
            data-testid="import-from-url-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Import from URL
          </Button>
          <Button
            onClick={() => setEditor({})}
            className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold"
            data-testid="new-it-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Product
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <KpiCard label="Total products" value={stats.total} accent="#023E8A" icon={Tag} />
        <KpiCard label="Published" value={stats.published} accent="#16A34A" icon={Eye} />
        <KpiCard label="Featured" value={stats.featured} accent="#FF6B35" icon={Star} />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#6C757D] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, name, description…"
            className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00B4D8]"
            data-testid="it-catalogue-search"
          />
        </div>
        <select
          value={filterBrand}
          onChange={(e) => setFilterBrand(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          data-testid="it-catalogue-brand-filter"
        >
          <option value="">All brands</option>
          {dynamicBrands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          data-testid="it-catalogue-category-filter"
        >
          <option value="">All categories</option>
          {dynamicCategories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <span className="text-xs text-[#6C757D]">{filtered.length} of {rows.length}</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Image</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">SKU</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Brand</th>
              <th className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Category</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Public</th>
              <th className="text-center px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Featured</th>
              <th className="text-right px-4 py-2.5 text-xs font-bold uppercase text-[#6C757D]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[#6C757D]">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-[#6C757D]">
                <Laptop className="w-10 h-10 mx-auto mb-2 opacity-40" />
                No products. Click <b className="text-[#FF6B35]">New Product</b> to add the first one.
              </td></tr>
            )}
            {!loading && filtered.map((p) => {
              const catLabel = (CATEGORIES.find((c) => c.key === p.category) || {}).label || p.category;
              return (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    {p.hero_image_url ? (
                      <img src={p.hero_image_url} alt="" className="w-14 h-10 object-cover rounded-md border border-gray-200" />
                    ) : (
                      <div className="w-14 h-10 rounded-md bg-slate-100 border border-gray-200 flex items-center justify-center">
                        <ImageIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-[#023E8A] font-semibold">{p.sku}</td>
                  <td className="px-4 py-3 text-[#2B2D42]">
                    <div className="font-medium line-clamp-1">{p.name}</div>
                    <div className="text-[11px] text-[#6C757D] line-clamp-1">{p.short_description}</div>
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{p.brand}</Badge></td>
                  <td className="px-4 py-3 text-[#6C757D]">{catLabel}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => togglePublic(p)}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition ${
                        p.is_public ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'
                      }`}
                      title={p.is_public ? 'Public — click to hide' : 'Hidden — click to publish'}
                      data-testid={`toggle-public-${p.sku}`}
                    >
                      {p.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleFeatured(p)}
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition ${
                        p.is_featured ? 'text-amber-500 hover:bg-amber-50' : 'text-slate-300 hover:bg-slate-100'
                      }`}
                      title={p.is_featured ? 'Featured' : 'Mark as featured'}
                      data-testid={`toggle-featured-${p.sku}`}
                    >
                      <Star className={`w-4 h-4 ${p.is_featured ? 'fill-amber-400' : ''}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1 relative">
                        <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pdfMenuFor && pdfMenuFor.id === p.id) {
                              setPdfMenuFor(null);
                              return;
                            }
                            // Decide open-up vs open-down based on viewport space below the button.
                            const rect = e.currentTarget.getBoundingClientRect();
                            const estMenuH = 60 + (companies.length || 1) * 48; // header + rows
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const openUp = spaceBelow < estMenuH + 16;
                            setPdfMenuFor({ id: p.id, openUp });
                          }}
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#FF6B35] hover:bg-orange-50"
                          title="Download spec-sheet PDF"
                          data-testid={`pdf-${p.sku}`}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        {pdfMenuFor && pdfMenuFor.id === p.id && companies.length > 0 && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            className={`absolute right-0 z-30 w-64 rounded-xl border border-slate-200 bg-white shadow-xl p-1.5 ${
                              pdfMenuFor.openUp ? 'bottom-9' : 'top-9'
                            }`}
                            data-testid={`pdf-menu-${p.sku}`}
                          >
                            <div className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-[#6C757D] border-b border-slate-100">
                              Brand the PDF as…
                            </div>
                            {companies.map((c) => (
                              <button
                                key={c.id}
                                onClick={() => { setPdfMenuFor(null); downloadProductPdf(p.id, c.id); }}
                                className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-50 flex items-center gap-2.5"
                                data-testid={`pdf-company-${c.id}`}
                              >
                                <span
                                  className="w-3.5 h-3.5 rounded-sm border border-slate-200 flex-shrink-0"
                                  style={{ backgroundColor: c.theme_color || '#0F172A' }}
                                  title={c.theme_color || ''}
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-[13px] font-bold text-[#0F172A] truncate">{c.trading_name || c.legal_name}</div>
                                  <div className="text-[10px] text-[#6C757D] truncate">TRN {c.trn || '—'}</div>
                                </div>
                                {c.is_default && (
                                  <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                    Default
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {p.master_product_id && (
                        <a
                          href={`/admin/products/${p.master_product_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50"
                          title="Open in Master Product Registry — manage prices, tiers, supplier costs"
                          data-testid={`mpr-link-${p.sku}`}
                        >
                          <Tag className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => setGalleryFor(p)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-violet-600 hover:bg-violet-50"
                        title="Manage gallery images"
                        data-testid={`gallery-${p.sku}`}
                      >
                        <ImageIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditor(p)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-[#023E8A] hover:bg-blue-50"
                        title="Edit"
                        data-testid={`edit-${p.sku}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleting(p)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        title="Delete"
                        data-testid={`delete-${p.sku}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Editor Modal */}
      {editor !== null && (
        <ITProductEditor
          existing={(editor.id || editor._is_imported) ? editor : null}
          forceCreate={!!editor._is_imported}
          token={token}
          brands={dynamicBrands}
          categories={dynamicCategories}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); fetchProducts(); }}
        />
      )}

      {/* Gallery Manager Modal — quick visual delete + set-hero */}
      {galleryFor && (
        <GalleryManagerModal
          product={galleryFor}
          token={token}
          onClose={() => setGalleryFor(null)}
          onChanged={(updated) => {
            // Live-merge the updated product back into the list
            setRows((prev) => prev.map((r) => r.id === updated.id ? { ...r, ...updated } : r));
            setGalleryFor((prev) => prev ? { ...prev, ...updated } : prev);
          }}
        />
      )}

      {/* Smart Import from URL */}
      {importOpen && (
        <SmartImportModal
          token={token}
          onClose={() => setImportOpen(false)}
          onDraft={(draft) => {
            setImportOpen(false);
            // Open the editor pre-filled with the draft
            setEditor({ ...draft, _is_imported: true });
          }}
        />
      )}

      {/* Delete confirm */}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="it-delete-dialog">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-xl flex items-center justify-between">
              <h3 className="text-base font-bold">Delete IT Product</h3>
              <button onClick={() => setDeleting(null)} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#2B2D42] mb-4">
                This will permanently remove the product from both the public catalogue and the admin registry.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
                <div><b className="font-mono text-[#023E8A]">{deleting.sku}</b></div>
                <div className="text-[#2B2D42]">{deleting.name}</div>
              </div>
            </div>
            <div className="px-6 py-3 bg-slate-50 border-t flex justify-end gap-2 rounded-b-xl">
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const KpiCard = ({ label, value, accent, icon: Icon }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}15` }}>
      <Icon className="w-5 h-5" style={{ color: accent }} />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#6C757D] font-bold">{label}</div>
      <div className="text-2xl font-extrabold text-[#2B2D42]">{value}</div>
    </div>
  </div>
);


// ============================================================================
// EDITOR
// ============================================================================
const blankForm = {
  sku: '', name: '', brand: 'Lenovo', category: 'business_laptops',
  short_description: '', marketing_copy_html: '', overview_html: '',
  hero_image_url: '', gallery_urls: [],
  specs: [], ports: [], ports_image_url: '',
  starting_from_aed: '', is_public: true, is_featured: false,
  featured_order: 999, tags: [], markup_pct: 10, manual_supplier_cost_aed: '',
};

const SPEC_CATEGORIES = ['Performance', 'Connectivity', 'Design', 'Sustainability', 'Other'];

const ITProductEditor = ({ existing, forceCreate = false, token, brands, categories, onClose, onSaved }) => {
  const { toast } = useToast();
  const isEdit = !!existing && !forceCreate;
  const [form, setForm] = useState(() => {
    if (!existing) return { ...blankForm };
    return {
      ...blankForm,
      ...existing,
      gallery_urls: existing.gallery_urls || [],
      specs: existing.specs || [],
      ports: existing.ports || [],
      ports_image_url: existing.ports_image_url || '',
      tags: existing.tags || [],
      overview_html: existing.overview_html || '',
      starting_from_aed: existing.starting_from_aed ?? '',
    };
  });
  const [saving, setSaving] = useState(false);

  // Standalone "label" mirror for the Category input so the admin sees a
  // friendly label while we silently slug to a snake_case key under the hood.
  const _humanizeKey = (k) => (k || '').split('_').filter(Boolean).map((p) => p[0].toUpperCase() + p.slice(1)).join(' ');
  const _initialCatLabel = () => {
    const found = (categories || CATEGORIES).find((c) => c.key === form.category);
    return found?.label || _humanizeKey(form.category);
  };
  const [categoryLabel, setCategoryLabel] = useState(_initialCatLabel);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addSpec = () => set('specs', [...form.specs, { label: '', value: '', category: 'Performance' }]);
  const updateSpec = (i, k, v) => set('specs', form.specs.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeSpec = (i) => set('specs', form.specs.filter((_, idx) => idx !== i));

  const addPort = () => set('ports', [...(form.ports || []), { label: '', value: '' }]);
  const updatePort = (i, k, v) => set('ports', (form.ports || []).map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removePort = (i) => set('ports', (form.ports || []).filter((_, idx) => idx !== i));

  // Gallery editing happens inline via the visual grid (Set as hero / Remove buttons).

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim() || !form.short_description.trim()) {
      toast({ variant: 'destructive', title: 'Missing fields', description: 'SKU, name and short description are required.' });
      return;
    }
    const payload = {
      ...form,
      specs: form.specs.filter((s) => s.label.trim() && s.value.trim()),
      ports: (form.ports || []).filter((s) => s.label.trim() && s.value.trim()),
      gallery_urls: form.gallery_urls.filter((g) => g && g.trim()),
      tags: (form.tags || []).filter(Boolean),
      starting_from_aed: form.starting_from_aed === '' ? null : Number(form.starting_from_aed),
      manual_supplier_cost_aed:
        form.manual_supplier_cost_aed === '' || form.manual_supplier_cost_aed === null || form.manual_supplier_cost_aed === undefined
          ? null
          : Number(form.manual_supplier_cost_aed),
      markup_pct:
        form.markup_pct === '' || form.markup_pct === null || form.markup_pct === undefined
          ? null
          : Number(form.markup_pct),
      featured_order: Number(form.featured_order) || 999,
    };
    setSaving(true);
    try {
      if (isEdit) {
        await axios.patch(`${API_URL}/api/admin/it-catalogue/${existing.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: 'Product updated', description: `${form.sku} — ${form.name}` });
      } else {
        await axios.post(`${API_URL}/api/admin/it-catalogue/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({ title: 'Product created', description: `${form.sku} — ${form.name}` });
      }
      onSaved();
    } catch (e) {
      toast({
        variant: 'destructive',
        title: isEdit ? 'Update failed' : 'Create failed',
        description: e.response?.data?.detail || e.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="it-product-editor">
      <div className="bg-white sm:rounded-2xl w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-[#023E8A] to-[#00B4D8] text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5">IT Solutions · Catalogue</div>
            <h3 className="text-lg font-extrabold">{isEdit ? 'Edit product' : 'New product'}</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-6">
          {/* Identity */}
          <Section title="Identity">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="SKU *" value={form.sku} onChange={(v) => set('sku', v)} disabled={isEdit} testid="editor-sku" />
              <Field label="Brand *">
                <input
                  list="editor-brand-list"
                  value={form.brand}
                  onChange={(e) => set('brand', e.target.value)}
                  placeholder="Lenovo, HP, Dell… or type a new brand"
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="editor-brand"
                />
                <datalist id="editor-brand-list">
                  {(brands || BRANDS).map((b) => <option key={b} value={b} />)}
                </datalist>
                <div className="text-[10px] text-[#94a3b8] mt-1">
                  Type any brand name — new brands appear in filters automatically.
                </div>
              </Field>
              <Field label="Category *">
                <input
                  list="editor-category-list"
                  value={categoryLabel}
                  onChange={(e) => {
                    const typed = e.target.value;
                    setCategoryLabel(typed);
                    const match = (categories || CATEGORIES).find(
                      (c) => c.label.toLowerCase() === typed.toLowerCase()
                    );
                    if (match) {
                      set('category', match.key);
                    } else {
                      const slug = typed
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_|_$/g, '');
                      set('category', slug || '');
                    }
                  }}
                  placeholder="Business Laptops, Storage Drives, etc."
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="editor-category"
                />
                <datalist id="editor-category-list">
                  {(categories || CATEGORIES).map((c) => <option key={c.key} value={c.label} />)}
                </datalist>
                <div className="text-[10px] text-[#94a3b8] mt-1">
                  Pick from the list or type a new category — key auto-slugged to{' '}
                  <span className="font-mono text-[#475569]">{form.category || '—'}</span>
                </div>
              </Field>
            </div>
            <Field label="Name *" value={form.name} onChange={(v) => set('name', v)} testid="editor-name" />
            <Field label="Short description (for cards) *">
              <textarea
                rows={2}
                value={form.short_description}
                onChange={(e) => set('short_description', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="editor-short-description"
              />
            </Field>
          </Section>

          {/* Master Product Registry link — shown when product is mirrored into P4 */}
          {isEdit && form.master_product_id && (
            <div
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
              data-testid="mpr-link-banner"
            >
              <div className="w-10 h-10 rounded-lg bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                <Tag className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 mb-0.5">
                  Master Product Registry · P4
                </div>
                <div className="text-sm font-bold text-[#0F172A]">
                  This product is live in the global registry.
                </div>
                <div className="text-[12px] text-[#475569] leading-relaxed mt-1">
                  Customer-specific pricing, supplier costs and historical price tiers are managed
                  from the MPR detail page. Invoices, quotations and POs auto-discover this product
                  by SKU.
                </div>
              </div>
              <a
                href={`/admin/products/${form.master_product_id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold whitespace-nowrap"
                data-testid="open-mpr-detail-link"
              >
                Open price manager <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* Helper banner: how the public tabs map to the sections below */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start gap-3">
            <Sparkles className="w-4 h-4 text-[#FF6B35] mt-0.5 flex-shrink-0" />
            <div className="text-[12px] text-[#475569] leading-relaxed">
              <b className="text-[#0F172A]">Public detail page tabs.</b> Each section below maps 1-to-1 with a tab on
              <code className="px-1.5 py-0.5 bg-white border border-slate-200 rounded mx-1">/it-solutions/{form.sku || 'sku'}</code>:
              <span className="ml-2 inline-flex gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold">Overview</span>
                <span className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold">Tech Specs</span>
                <span className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold">Ports & Slots</span>
                <span className="px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-bold text-[#6C757D]">About BMP <span className="font-normal">(global)</span></span>
              </span>
            </div>
          </div>

          {/* Visibility */}
          <Section title="Visibility & display">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Toggle label="Published on public page" value={form.is_public} onChange={(v) => set('is_public', v)} testid="editor-is-public" />
              <Toggle label="Featured" value={form.is_featured} onChange={(v) => set('is_featured', v)} testid="editor-is-featured" />
              <Field label="Featured order (1 = first)">
                <input
                  type="number"
                  value={form.featured_order}
                  onChange={(e) => set('featured_order', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </Field>
            </div>
            <Field label="Tags (comma-separated)">
              <input
                value={(form.tags || []).join(', ')}
                onChange={(e) => set('tags', e.target.value.split(',').map((t) => t.trim()).filter(Boolean))}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="enterprise, security, ultralight"
              />
            </Field>
            <Field label="Starting from (AED — internal hint, hidden on public page)">
              <input
                type="number"
                value={form.starting_from_aed}
                onChange={(e) => set('starting_from_aed', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="editor-starting-from-aed"
              />

              {/* Manual supplier-cost calculator — always visible */}
              <div className="mt-2 rounded-lg bg-amber-50/60 border border-amber-200 p-3 text-[12px] text-[#475569] leading-relaxed">
                <div className="font-bold uppercase tracking-widest text-[10px] text-amber-700 mb-2 flex items-center gap-1.5">
                  <Tag className="w-3 h-3" /> Manual pricing · supplier cost → BMP price
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#6C757D]">Supplier cost (AED)</label>
                    <input
                      type="number"
                      value={form.manual_supplier_cost_aed ?? ''}
                      placeholder="e.g. 4500"
                      onChange={(e) => {
                        const v = e.target.value;
                        const cost = v === '' ? null : Number(v);
                        set('manual_supplier_cost_aed', cost);
                        const pct = Number(form.markup_pct ?? 10);
                        if (cost !== null && !Number.isNaN(cost)) {
                          set('starting_from_aed', Number((cost * (1 + (pct || 0) / 100)).toFixed(2)));
                        }
                      }}
                      className="mt-1 w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-white"
                      data-testid="editor-manual-supplier-cost"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[#6C757D]">BMP markup (%)</label>
                    <input
                      type="number"
                      value={form.markup_pct ?? 10}
                      onChange={(e) => {
                        const pct = Number(e.target.value);
                        set('markup_pct', pct);
                        const base = form.manual_supplier_cost_aed ?? form.source_price_aed;
                        if (base) {
                          set('starting_from_aed', Number((base * (1 + (pct || 0) / 100)).toFixed(2)));
                        }
                      }}
                      className="mt-1 w-full px-2 py-1.5 border border-amber-200 rounded text-sm bg-white"
                      data-testid="editor-markup-pct"
                    />
                  </div>
                  <div className="rounded bg-white border border-amber-300 px-3 py-2">
                    <div className="text-[9px] uppercase tracking-widest font-bold text-amber-700">BMP price (AED)</div>
                    <div className="text-base font-extrabold text-[#0F172A]" data-testid="editor-computed-bmp-price">
                      {form.starting_from_aed
                        ? `AED ${Number(form.starting_from_aed).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                        : '—'}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-[#94a3b8] mt-2">
                  Internal only · hidden on the public detail page. Used for institutional quotations and PO pricing in MPR.
                </div>
              </div>

              {form.source_price && (
                <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-[#6C757D] leading-relaxed">
                  <div className="font-bold uppercase tracking-widest text-[10px] text-[#023E8A] mb-1.5">Smart-import pricing trail</div>
                  <div>Source price: <b className="text-[#2B2D42]">{form.source_currency || ''} {Number(form.source_price).toLocaleString()}</b></div>
                  {form.source_price_aed && (
                    <div>Source in AED: <b className="text-[#2B2D42]">AED {Number(form.source_price_aed).toLocaleString()}</b></div>
                  )}
                  {form.source_url && (
                    <div className="mt-1 truncate">
                      <a href={form.source_url} target="_blank" rel="noreferrer" className="text-[#00B4D8] hover:underline">
                        Open source page ↗
                      </a>
                    </div>
                  )}
                </div>
              )}
            </Field>
          </Section>

          {/* Media — visual gallery editor */}
          <Section title="Media · Gallery & Hero">
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-widest text-[#023E8A]">Product gallery</div>
                  <div className="text-[12px] text-[#6C757D] mt-0.5">
                    Click <Star className="w-3 h-3 inline -mt-0.5 fill-amber-400 text-amber-500" /> to set the hero image ·
                    click <X className="w-3 h-3 inline -mt-0.5 text-red-500" /> to remove a bad photo. PDFs auto-update.
                  </div>
                </div>
                <div className="text-[11px] text-[#6C757D]">
                  {(form.gallery_urls || []).filter(Boolean).length} image{(form.gallery_urls || []).filter(Boolean).length === 1 ? '' : 's'}
                </div>
              </div>

              {(form.gallery_urls || []).filter(Boolean).length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 text-center text-sm text-[#6C757D]">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  No gallery images yet. Paste a URL below to add one.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="editor-gallery-grid">
                  {(form.gallery_urls || []).map((g, i) => {
                    if (!g) return null;
                    const isHero = g === form.hero_image_url;
                    return (
                      <div
                        key={`${g}-${i}`}
                        className={`relative group rounded-lg overflow-hidden border-2 bg-white transition ${
                          isHero ? 'border-amber-400 shadow-md ring-2 ring-amber-200' : 'border-slate-200 hover:border-[#023E8A]'
                        }`}
                        data-testid={`gallery-tile-${i}`}
                      >
                        <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center">
                          <img
                            src={g}
                            alt={`Gallery ${i + 1}`}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
                          />
                        </div>

                        {/* Hero badge */}
                        {isHero && (
                          <div className="absolute top-1.5 left-1.5 bg-amber-400 text-[9px] font-extrabold uppercase tracking-widest text-amber-900 px-1.5 py-0.5 rounded shadow flex items-center gap-1">
                            <Star className="w-2.5 h-2.5 fill-amber-900" /> Hero
                          </div>
                        )}

                        {/* Action buttons (always visible on touch; hover overlay on desktop) */}
                        <div className="absolute top-1.5 right-1.5 flex flex-col gap-1.5">
                          {!isHero && (
                            <button
                              type="button"
                              onClick={() => set('hero_image_url', g)}
                              title="Set as hero image"
                              data-testid={`gallery-set-hero-${i}`}
                              className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-white/95 border border-slate-200 shadow-sm text-amber-500 hover:bg-amber-50 hover:text-amber-600"
                            >
                              <Star className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const next = (form.gallery_urls || []).filter((u, idx) => !(u === g && idx === i));
                              set('gallery_urls', next);
                              if (isHero) {
                                set('hero_image_url', next[0] || '');
                              }
                            }}
                            title="Remove this image"
                            data-testid={`gallery-delete-${i}`}
                            className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-white/95 border border-red-200 shadow-sm text-red-500 hover:bg-red-50 hover:text-red-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Index pill */}
                        <div className="absolute bottom-1.5 left-1.5 text-[9px] font-mono text-white bg-black/60 px-1.5 py-0.5 rounded">
                          {String(i + 1).padStart(2, '0')} / {(form.gallery_urls || []).filter(Boolean).length.toString().padStart(2, '0')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add new gallery image */}
              <div className="mt-4 flex items-center gap-2">
                <input
                  type="url"
                  placeholder="https://… (paste a new image URL and press Enter)"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                  data-testid="editor-gallery-add-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = e.currentTarget.value.trim();
                      if (!v) return;
                      const next = [...(form.gallery_urls || []), v];
                      set('gallery_urls', next);
                      if (!form.hero_image_url) set('hero_image_url', v);
                      e.currentTarget.value = '';
                    }
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const inp = document.querySelector('[data-testid="editor-gallery-add-input"]');
                    if (!inp) return;
                    const v = inp.value.trim();
                    if (!v) return;
                    const next = [...(form.gallery_urls || []), v];
                    set('gallery_urls', next);
                    if (!form.hero_image_url) set('hero_image_url', v);
                    inp.value = '';
                  }}
                  data-testid="editor-gallery-add-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add image
                </Button>
              </div>

              {/* Manual hero URL (advanced — kept for backwards compatibility) */}
              <details className="mt-3">
                <summary className="text-[11px] text-[#6C757D] cursor-pointer hover:text-[#023E8A]">
                  Advanced · Hero image URL (manual entry)
                </summary>
                <input
                  value={form.hero_image_url || ''}
                  onChange={(e) => set('hero_image_url', e.target.value)}
                  className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                  placeholder="https://…"
                  data-testid="editor-hero-image"
                />
              </details>
            </div>
          </Section>

          {/* ====== PUBLIC TAB 1 · OVERVIEW ====== */}
          <Section title="Overview tab — long-form description">
            <Field label="Overview content (HTML supported)">
              <textarea
                rows={6}
                value={form.overview_html || form.marketing_copy_html || ''}
                onChange={(e) => set('overview_html', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono text-[12px]"
                placeholder='<p>The flagship ThinkPad X1 Carbon Gen 12 delivers …</p>&#10;<ul><li>AI-powered productivity</li><li>Military-grade durability</li></ul>'
                data-testid="editor-overview-html"
              />
              <p className="text-[11px] text-[#6C757D] mt-1.5 leading-relaxed">
                Appears under the <b>Overview</b> tab. Plain HTML (basic tags only — &lt;p&gt;, &lt;b&gt;, &lt;ul&gt;, &lt;li&gt;).
                Images from the Media section above are rendered as a 2×2 grid below this copy.
              </p>
            </Field>
          </Section>

          {/* ====== PUBLIC TAB 2 · TECH SPECS ====== */}
          <Section title="Tech Specs tab — full specifications (grouped)">
            <p className="text-[11px] text-[#6C757D] -mt-1 leading-relaxed">
              Appears under the <b>Tech Specs</b> tab as label/value rows <b>grouped by category</b>
              (Performance · Connectivity · Design · Sustainability) — Lenovo-style accordions on the public page.
              Also drives the highlight chips at the hero (CPU · RAM · Storage · Display · Battery · Camera).
            </p>
            <div className="space-y-2">
              {(form.specs || []).map((s, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[150px_180px_1fr_auto] gap-2 items-center">
                  <select
                    value={s.category || 'Performance'}
                    onChange={(e) => updateSpec(i, 'category', e.target.value)}
                    className="px-2 py-2 border border-gray-200 rounded-lg text-xs font-bold text-[#023E8A] bg-slate-50"
                    data-testid={`spec-category-${i}`}
                  >
                    {SPEC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    value={s.label}
                    onChange={(e) => updateSpec(i, 'label', e.target.value)}
                    placeholder="Label (e.g. Processor)"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold"
                    data-testid={`spec-label-${i}`}
                  />
                  <input
                    value={s.value}
                    onChange={(e) => updateSpec(i, 'value', e.target.value)}
                    placeholder="Value (e.g. Intel® Core™ Ultra 7 165H)"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    data-testid={`spec-value-${i}`}
                  />
                  <button onClick={() => removeSpec(i)} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSpec} data-testid="editor-add-spec-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add specification
              </Button>
            </div>
          </Section>

          {/* ====== PUBLIC TAB 3 · PORTS & SLOTS ====== */}
          <Section title="Ports & Slots tab — diagram + numbered legend">
            <p className="text-[11px] text-[#6C757D] -mt-1 leading-relaxed">
              Lenovo-style layout: a <b>diagram image with numbered callouts</b> on the left and a
              <b> numbered legend</b> on the right. The Smart Import tries to auto-extract both from the source URL.
            </p>
            <Field label="Ports diagram image URL (showing numbered callouts 1, 2, 3 …)">
              <input
                value={form.ports_image_url || ''}
                onChange={(e) => set('ports_image_url', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="https://… (e.g. the Lenovo ports diagram)"
                data-testid="editor-ports-image"
              />
              {form.ports_image_url && (
                <img
                  src={form.ports_image_url}
                  alt="Ports diagram"
                  className="mt-2 max-w-md max-h-48 object-contain rounded-md border border-gray-200 bg-white p-2"
                />
              )}
            </Field>
            <Field label="Numbered legend (matches the diagram callouts)">
              <div className="space-y-2 mt-1">
                {(form.ports || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-[80px_1fr_auto] gap-2 items-center">
                    <input
                      value={s.label}
                      onChange={(e) => updatePort(i, 'label', e.target.value)}
                      placeholder="Number"
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold text-center text-[#FF6B35]"
                      data-testid={`port-label-${i}`}
                    />
                    <input
                      value={s.value}
                      onChange={(e) => updatePort(i, 'value', e.target.value)}
                      placeholder="Description (e.g. Thunderbolt 4 / USB-C with DisplayPort 1.4 and PD 3.0)"
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      data-testid={`port-value-${i}`}
                    />
                    <button onClick={() => removePort(i)} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addPort} data-testid="editor-add-port-btn">
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add numbered entry
                </Button>
              </div>
            </Field>
          </Section>

          {/* ====== PUBLIC TAB 4 · ABOUT BMP (global, not per-product) ====== */}
          <Section title="About BMP tab — global section">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-[12px] text-[#6C757D] leading-relaxed">
              The <b className="text-[#0F172A]">About BMP</b> tab is a brand-level block shared across all products
              (Direct sourcing · Multi-country delivery · Authorised channel). It is not edited per-product.
              To change its copy globally, contact development — a future &quot;Brand Settings&quot; admin page will expose it.
            </div>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex items-center justify-between sm:rounded-b-2xl">
          <span className="text-xs text-[#6C757D]">All changes apply immediately to <a href="/it-solutions" target="_blank" rel="noreferrer" className="text-[#00B4D8] hover:underline">/it-solutions <ArrowUpRight className="inline w-3 h-3" /></a></span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold"
              data-testid="editor-save-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />{isEdit ? 'Save changes' : 'Create product'}</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <div className="text-[11px] font-bold uppercase tracking-widest text-[#FF6B35] border-b border-orange-100 pb-1.5">{title}</div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, disabled, testid, children }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{label}</label>
    {children || (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-500"
        data-testid={testid}
      />
    )}
  </div>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{label}</label>
    <button
      onClick={() => onChange(!value)}
      className={`mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition w-full justify-between ${
        value
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-slate-50 border-slate-200 text-slate-500'
      }`}
      data-testid={testid}
    >
      <span>{value ? 'Yes' : 'No'}</span>
      <span className={`w-9 h-5 rounded-full relative ${value ? 'bg-emerald-500' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${value ? 'left-[18px]' : 'left-0.5'}`} />
      </span>
    </button>
  </div>
);

// ============================================================================
// SMART IMPORT MODAL — fetch a partner URL and pre-fill the editor
// ============================================================================
const SmartImportModal = ({ token, onClose, onDraft }) => {
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const run = async () => {
    const u = url.trim();
    if (!u.startsWith('http')) {
      setErrorMsg('Please paste a full URL starting with https://');
      return;
    }
    setErrorMsg('');
    setDiagResult(null);
    setLoading(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/it-catalogue/import-from-url`,
        { url: u },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      toast({
        title: 'Draft ready',
        description: `${r.data.brand || '—'} · ${r.data.name?.slice(0, 60) || 'unnamed'}`,
      });
      onDraft(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Smart import failed.';
      const status = e.response?.status ? ` (HTTP ${e.response.status})` : '';
      setErrorMsg(`${detail}${status}`);
    } finally {
      setLoading(false);
    }
  };

  const runDiag = async () => {
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/it-catalogue/import-diag`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      setDiagResult(r.data);
    } catch (e) {
      setDiagResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="smart-import-modal"
    >
      <div className="bg-white sm:rounded-2xl w-full max-w-2xl shadow-2xl flex flex-col">
        <div className="px-6 py-4 bg-gradient-to-r from-[#023E8A] to-[#00B4D8] text-white sm:rounded-t-2xl flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-0.5 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Smart Import · IT Catalogue
            </div>
            <h3 className="text-lg font-extrabold">Import from supplier URL</h3>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="smart-import-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">Partner product URL</label>
            <div className="mt-1 flex gap-2">
              <div className="relative flex-1">
                <Link2 className="w-4 h-4 text-[#6C757D] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.lenovo.com/ae/en/p/laptops/…"
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#023E8A]"
                  data-testid="smart-import-url-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
                  autoFocus
                />
              </div>
              <Button
                onClick={run}
                disabled={loading || !url.trim()}
                className="bg-[#023E8A] hover:bg-[#012B5C] text-white font-bold"
                data-testid="smart-import-run-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Extract</>}
              </Button>
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <div className="flex items-start gap-3 mb-2">
              <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-[#2B2D42]">How it works</div>
                <ul className="text-[12px] text-[#6C757D] leading-relaxed list-disc pl-4 mt-1 space-y-0.5">
                  <li>Paste a Lenovo, HP or Dell product page URL.</li>
                  <li>We scrape name, hero image, description and specs from the page.</li>
                  <li>If a price is exposed, we auto-apply <b className="text-[#023E8A]">+10% markup</b> as the BMP starting price (editable).</li>
                  <li>The draft opens in the editor for review — nothing is published until you save.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-relaxed flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>
              All trademarks, images and specifications are property of their respective brands.
              <b> BMP is an official partner</b> — the catalogue reflects partner-supplied data and is sold institutionally on request.
            </span>
          </div>

          {errorMsg && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-2">
              <div className="font-mono text-[12px] leading-relaxed break-all">{errorMsg}</div>
              <div className="flex items-center gap-2 pt-1 border-t border-red-100">
                <button
                  type="button"
                  onClick={runDiag}
                  disabled={diagLoading}
                  className="text-[11px] font-bold uppercase tracking-widest text-red-700 hover:text-red-900 underline underline-offset-2"
                  data-testid="smart-import-diag-btn"
                >
                  {diagLoading ? 'Running diagnostics…' : 'Run diagnostics'}
                </button>
                <span className="text-[10px] text-red-500">
                  Checks env keys, outbound network &amp; LLM availability.
                </span>
              </div>
              {diagResult && (
                <pre className="text-[10px] bg-white/70 rounded p-2 border border-red-100 overflow-x-auto max-h-40">
{JSON.stringify(diagResult, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t flex items-center justify-between sm:rounded-b-2xl">
          <span className="text-[11px] text-[#6C757D]">Supports Lenovo · HP · Dell — other brands fall back to AI extraction.</span>
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="smart-import-cancel-btn">Cancel</Button>
        </div>
      </div>
    </div>
  );
};

export default ITCatalogueAdmin;


// ============================================================================
// GALLERY MANAGER MODAL — visual delete + set-hero (operates live on backend)
// ============================================================================
const GalleryManagerModal = ({ product, token, onClose, onChanged }) => {
  const { toast } = useToast();
  const [gallery, setGallery] = useState(product.gallery_urls || []);
  const [hero, setHero] = useState(product.hero_image_url || '');
  const [busy, setBusy] = useState(null); // url currently being mutated

  const removeImage = async (url) => {
    if (!window.confirm('Remove this image from the gallery? This also updates the PDF spec-sheet.')) return;
    setBusy(url);
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/it-catalogue/${product.id}/gallery-image`,
        { params: { url }, headers: { Authorization: `Bearer ${token}` } },
      );
      const newGallery = r.data?.gallery_urls || [];
      const newHero = r.data?.hero_image_url || '';
      setGallery(newGallery);
      setHero(newHero);
      onChanged({ id: product.id, gallery_urls: newGallery, hero_image_url: newHero });
      toast({ title: 'Image removed', description: `${newGallery.length} image(s) remaining. PDF will reflect the change.` });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  const setAsHero = async (url) => {
    setBusy(url);
    try {
      await axios.post(
        `${API_URL}/api/admin/it-catalogue/${product.id}/set-hero`,
        null,
        { params: { url }, headers: { Authorization: `Bearer ${token}` } },
      );
      setHero(url);
      onChanged({ id: product.id, hero_image_url: url, gallery_urls: gallery });
      toast({ title: 'Hero image updated' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Set hero failed', description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="gallery-manager-modal"
    >
      <div className="bg-white sm:rounded-2xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-violet-700 to-violet-900 text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-violet-200 mb-0.5">Gallery manager</div>
            <h3 className="text-lg font-extrabold truncate max-w-[60vw]">{product.brand} · {product.name}</h3>
            <div className="text-[11px] text-violet-200 mt-0.5 font-mono">{product.sku}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="gallery-manager-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-4">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
            <div>
              <b>Review &amp; clean-up.</b> Delete any photo that looks broken, cropped or off-brand.
              The product PDF spec-sheet reads the gallery live, so it auto-updates the next time it&apos;s generated.
            </div>
          </div>

          {gallery.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-[#6C757D]">
              <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              No gallery images. Open the full editor to add new ones.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-manager-grid">
              {gallery.map((url, i) => {
                const isHero = url === hero;
                const isBusy = busy === url;
                return (
                  <div
                    key={`${url}-${i}`}
                    className={`relative group rounded-lg overflow-hidden border-2 bg-white transition ${
                      isHero ? 'border-amber-400 ring-2 ring-amber-200 shadow-md' : 'border-slate-200 hover:border-violet-500'
                    } ${isBusy ? 'opacity-50 pointer-events-none' : ''}`}
                    data-testid={`gm-tile-${i}`}
                  >
                    <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center">
                      <img
                        src={url}
                        alt={`Gallery ${i + 1}`}
                        className="w-full h-full object-contain"
                        onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
                      />
                    </div>
                    {isHero && (
                      <div className="absolute top-1.5 left-1.5 bg-amber-400 text-[9px] font-extrabold uppercase tracking-widest text-amber-900 px-1.5 py-0.5 rounded shadow flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-amber-900" /> Hero
                      </div>
                    )}
                    <div className="absolute top-1.5 right-1.5 flex flex-col gap-1.5">
                      {!isHero && (
                        <button
                          type="button"
                          onClick={() => setAsHero(url)}
                          title="Set as hero image"
                          data-testid={`gm-set-hero-${i}`}
                          className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-white/95 border border-slate-200 shadow-sm text-amber-500 hover:bg-amber-50 hover:text-amber-600"
                        >
                          <Star className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(url)}
                        title="Remove this image"
                        data-testid={`gm-delete-${i}`}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-md bg-white/95 border border-red-200 shadow-sm text-red-500 hover:bg-red-50 hover:text-red-600"
                      >
                        {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    <div className="absolute bottom-1.5 left-1.5 text-[9px] font-mono text-white bg-black/60 px-1.5 py-0.5 rounded">
                      {String(i + 1).padStart(2, '0')} / {gallery.length.toString().padStart(2, '0')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t flex items-center justify-between sm:rounded-b-2xl">
          <span className="text-[11px] text-[#6C757D]">
            {gallery.length} image(s) · Hero: {hero ? <span className="font-mono">{hero.split('/').pop()?.slice(0, 24)}…</span> : <i>none</i>}
          </span>
          <Button onClick={onClose} data-testid="gallery-manager-done-btn">Done</Button>
        </div>
      </div>
    </div>
  );
};
